//src/pages/pricing/components/PaymentModal.js

import React, { useEffect, useState, useRef } from 'react';
import { formatNumber, formatPrice } from '../utils/pricingUtils';
import { logger } from '../../../utils/logger';
import './PaymentModal.css';

/**
 * Payment modal for handling subscription payments
 */
const PaymentModal = ({
  isProcessing,
  selectedPlanDetails,
  agentCount,
  queryCount,
  documentCount,
  squareStatus,
  paymentError,
  onClose,
  onProcessPayment,
  isCurrentPlan,
  isCurrentActive,
  cardContainerKey
}) => {
  const [status, setStatus] = useState(squareStatus); // loading, ready, error
  const cardRef = useRef(null);
  // Create a unique ID for the Square card container
  const cardContainerId = `square-card-${cardContainerKey}`;

  // Update status when square status changes
  useEffect(() => {
    setStatus(squareStatus);
  }, [squareStatus]);
  
  // This function will completely remove the Square script from the page
  const removeSquareScript = () => {
    try {
      // Remove all Square scripts
      const scripts = document.querySelectorAll('script[src*="squarecdn.com"]');
      scripts.forEach(script => {
        script.parentNode.removeChild(script);
      });
      
      // Remove any global Square objects
      if (window.Square) {
        try {
          delete window.Square;
        } catch (e) {
          window.Square = undefined;
        }
      }
      
      logger.debug('Removed Square scripts and global objects');
    } catch (e) {
      logger.warn('Error removing Square scripts:', e);
    }
  };
  
  // Cleanup function for Square card
  const cleanupCard = () => {
    try {
      // If we have a card in our ref, destroy it
      if (cardRef.current) {
        logger.debug('Destroying existing Square card');
        cardRef.current.destroy();
        cardRef.current = null;
      }
      
      // Find and remove all Square iframes from the DOM
      const iframes = document.querySelectorAll('iframe[name*="square"]');
      iframes.forEach(iframe => {
        iframe.parentNode.removeChild(iframe);
      });
      
      // Also clear any existing card elements from the DOM
      document.querySelectorAll('[id^="square-card"]').forEach(container => {
        while (container.firstChild) {
          container.removeChild(container.firstChild);
        }
      });
      
      logger.debug('Cleaned up Square card elements');
    } catch (e) {
      logger.warn('Error cleaning up Square card:', e);
    }
  };
  
  // Load Square script and initialize once on mount, but only for custom plans
  useEffect(() => {
    // Skip Square initialization for non-custom plans
    if (selectedPlanDetails?.id !== 'custom') {
      return;
    }
    
    // Clean up any existing Square resources first
    cleanupCard();
    
    let mounted = true;
    
    const loadSquare = async () => {
      try {
        logger.debug(`Initializing Square payment form with container ID: ${cardContainerId}`);
        
        // Get credentials
        const SQUARE_APP_ID = process.env.REACT_APP_SQUARE_APP_ID;
        const SQUARE_LOCATION_ID = process.env.REACT_APP_SQUARE_LOCATION_ID;
        
        if (!SQUARE_APP_ID || !SQUARE_LOCATION_ID) {
          logger.error('Square credentials missing');
          if (mounted) setStatus('error');
          return;
        }
        
        // Load Square.js script
        // Always reload the script to ensure a fresh instance
        removeSquareScript();
        
        const script = document.createElement('script');
        script.src = process.env.NODE_ENV === 'production' 
          ? 'https://web.squarecdn.com/v1/square.js' 
          : 'https://sandbox.web.squarecdn.com/v1/square.js';
        
        script.onload = () => {
          if (mounted) {
            initializeCard(SQUARE_APP_ID, SQUARE_LOCATION_ID);
          }
        };
        
        script.onerror = () => {
          logger.error('Failed to load Square.js');
          if (mounted) setStatus('error');
        };
        
        document.body.appendChild(script);
      } catch (error) {
        logger.error('Error in Square setup:', error);
        if (mounted) setStatus('error');
      }
    };
    
    const initializeCard = async (appId, locationId) => {
      try {
        if (!window.Square) {
          if (mounted) setStatus('error');
          return;
        }
        
        // Initialize the card
        const payments = window.Square.payments(appId, locationId);
        const card = await payments.card();
        
        // Store the card instance in the ref
        if (mounted) {
          cardRef.current = card;
        }
        
        // Find the card container and attach
        const cardContainer = document.getElementById(cardContainerId);
        if (!cardContainer) {
          logger.error(`Card container not found: ${cardContainerId}`);
          if (mounted) setStatus('error');
          return;
        }
        
        await card.attach(`#${cardContainerId}`);
        if (mounted) setStatus('ready');
      } catch (error) {
        logger.error('Error in card initialization:', error);
        if (mounted) setStatus('error');
      }
    };
    
    loadSquare();
    
    // Cleanup function
    return () => {
      mounted = false;
      cleanupCard();
    };
  }, [selectedPlanDetails, cardContainerId]);
  
  // Ensure cleanup when component unmounts
  useEffect(() => {
    return () => {
      cleanupCard();
      removeSquareScript();
    };
  }, []);
  
  // Process payment when user clicks the button
  const handleSubmit = async () => {
    // For custom plans, validate card form
    if (selectedPlanDetails?.id === 'custom') {
      if (!cardRef.current) {
        logger.error('Card form not initialized');
        return;
      }
      
      // Log what type of action we're taking (new subscription vs upgrade)
      const trialActive = isCurrentPlan('free') && isCurrentActive;
      logger.debug(`Processing payment: Trial active: ${trialActive}, Current plan: ${isCurrentPlan(selectedPlanDetails?.id)}`);
      
      onProcessPayment(cardRef.current);
    } else {
      // For free trial or other plans, no card needed
      onProcessPayment();
    }
  };
  
  // Handler for modal close to ensure cleanup
  const handleClose = () => {
    if (isProcessing) return;
    
    // Do an immediate cleanup
    cleanupCard();
    // Remove Square script to ensure a fresh start next time
    removeSquareScript();
    
    onClose();
  };
  
  return (
    <div className="payment-modal-overlay" onClick={isProcessing ? null : handleClose}>
      <div className="payment-modal" onClick={(e) => e.stopPropagation()}>
        <button 
          className="close-modal-button"
          onClick={isProcessing ? null : handleClose}
          disabled={isProcessing}
        >
          &times;
        </button>
        
        <h2>
          {isCurrentPlan(selectedPlanDetails?.id) && isCurrentActive 
            ? 'Update Your Subscription' 
            : 'Complete Your Subscription'}
        </h2>
        
        {/* Plan Summary */}
        <div className="payment-plan-summary">
          <h3>{selectedPlanDetails?.name || 'Selected Plan'}</h3>
          <div className="payment-price">
            {typeof selectedPlanDetails?.price === 'number' 
              ? formatPrice(selectedPlanDetails.price) 
              : selectedPlanDetails?.price || 'Custom'} 
            <span className="payment-period">/month</span>
          </div>
          
          {selectedPlanDetails?.id === 'custom' && (
            <div className="custom-plan-details">
              <p>
                {isCurrentPlan(selectedPlanDetails.id) && isCurrentActive 
                  ? 'Updated plan details:' 
                  : 'Custom plan details:'}
              </p>
              <ul>
                <li>{agentCount} Agent{agentCount > 1 ? 's' : ''}</li>
                <li>{formatNumber(queryCount)} Queries per month</li>
                <li>{formatNumber(documentCount)} Documents</li>
              </ul>
            </div>
          )}
        </div>
        
        {/* Payment Form - Only show for custom plans */}
        {selectedPlanDetails?.id === 'custom' && (
          <div className="payment-form" key={`payment-form-${cardContainerKey}`}>
            <h4>
              {isCurrentPlan(selectedPlanDetails.id) && isCurrentActive 
                ? 'Payment Method for Updated Plan' 
                : 'Payment Method'}
            </h4>
            
            {/* Square Card Form */}
            {status === 'loading' && (
              <div className="card-loading">
                <p>Loading payment form...</p>
                <div className="loading-spinner"></div>
              </div>
            )}
            
            {status === 'error' && (
              <div className="card-error">
                <p>There was an error loading the payment form. Please try again.</p>
                <button onClick={() => window.location.reload()}>Reload</button>
              </div>
            )}
            
            <div id={cardContainerId} className={`square-card ${status === 'ready' ? 'ready' : ''}`}></div>
            
            {status === 'ready' && (
              <div className="test-card-info">
                <p></p>
              </div>
            )}
          </div>
        )}
        
        {/* Payment error */}
        {paymentError && (
          <div className="payment-error">
            <p>{paymentError}</p>
          </div>
        )}
        
        {/* Security notice and payment button */}
        <div className="payment-action-area">
          <div className="payment-security-note">
            <p>
              <span className="lock-icon">🔒</span> 
              {selectedPlanDetails?.id === 'free' 
                ? 'Your card will not be charged during the free trial period.' 
                : 'Your payment is secure. We use Square for secure payment processing.'}
            </p>
          </div>
          
          <button 
            className="payment-submit-button"
            onClick={handleSubmit}
            disabled={isProcessing || (selectedPlanDetails?.id === 'custom' && status !== 'ready')}
          >
            {isProcessing ? (
              <>
                <span className="processing-spinner"></span>
                Processing...
              </>
            ) : (
              selectedPlanDetails?.id === 'free' 
                ? 'Start Free Trial' 
                : isCurrentPlan(selectedPlanDetails?.id) && isCurrentActive
                  ? 'Update Plan'  
                  : 'Subscribe Now'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentModal; 